import React from 'react';
import { Button, DatePicker, Input, Space } from 'antd';
import moment from 'moment';

import { SearchOutlined } from '@ant-design/icons';

const DATE_INDEXES = new Set([
  'startDate',
  'dateScheduling',
  'createDate',
  'dateUploadAcceptTerm2',
  'confirmationDate',
  'billingDeadlineDate',
  'invoiceEmissionDate',
]);

const isDateColumn = (dataIndex) => DATE_INDEXES.has(dataIndex);

const joinDriverSearchText = (drivers) =>
  drivers
    .map(
      (driver) =>
        `${driver.userName || ''} ${driver.vehicle?.licensePlate || ''} ${
          driver.vehicle?.type?.name || ''
        }`
    )
    .join(' ');

const joinArraySearchText = (items) =>
  items.map((item) => item?.label || item?.name || item?.url || '').join(' ');

export const getTaskListColumnSearchText = (record, dataIndex) => {
  if (!record || !dataIndex) return '';
  const value =
    typeof dataIndex === 'string' && dataIndex.startsWith('customFields.')
      ? (record.customFields ?? record.CustomFields ?? {})[dataIndex.slice('customFields.'.length)]
      : record[dataIndex];
  if (value == null || value === '') return '';

  if (dataIndex === 'product' || dataIndex === 'group' || dataIndex === 'stage') {
    return String(value.name || '');
  }
  if (dataIndex === 'mainInstructor' || dataIndex === 'assistantInstructor') {
    return String(value.userName || '');
  }
  if (dataIndex === 'transportCompany') {
    return String(value.name || value.commercialName || '');
  }
  if (dataIndex === 'driverList' && Array.isArray(value)) return joinDriverSearchText(value);
  if (isDateColumn(dataIndex)) {
    return moment.utc(value).isValid() ? moment.utc(value).format('DD/MM/YYYY') : '';
  }
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (Array.isArray(value)) return joinArraySearchText(value);
  if (typeof value === 'object') return String(value.name || value.userName || value.label || '');
  return String(value);
};

export const matchesTaskListColumnFilter = (filterValue, record, dataIndex) => {
  if (filterValue == null || filterValue === '') return true;
  const haystack = getTaskListColumnSearchText(record, dataIndex);
  if (!haystack) return false;

  if (dataIndex === 'dealClientCNPJ') {
    const typedDigits = String(filterValue).replace(/\D/g, '');
    if (!typedDigits) return true;
    return haystack.replace(/\D/g, '').includes(typedDigits);
  }

  return haystack.toLowerCase().includes(String(filterValue).toLowerCase());
};

const renderSearchInput = (dataIndex, title, selectedKeys, setSelectedKeys, confirm) => {
  if (isDateColumn(dataIndex)) {
    return (
      <DatePicker
        format="DD/MM/YYYY"
        placeholder={title}
        value={selectedKeys[0] ? moment(selectedKeys[0], 'DD/MM/YYYY') : null}
        onChange={(_, dateString) => setSelectedKeys(dateString ? [dateString] : [])}
        style={{ marginBottom: 8, display: 'block', width: '100%' }}
      />
    );
  }

  return (
    <Input
      className={`task-list-column-search-${dataIndex}`}
      placeholder={title}
      value={selectedKeys[0]}
      onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
      onPressEnter={() => confirm()}
      style={{ marginBottom: 8, display: 'block' }}
    />
  );
};

export const getTaskListColumnSearchProps = (column) => {
  const dataIndex = column?.dataIndex;
  const title = column?.title || '';
  if (!dataIndex) return {};

  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="task-list-column-filter" style={{ padding: 8 }}>
        {renderSearchInput(dataIndex, title, selectedKeys, setSelectedKeys, confirm)}
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => confirm()}
            style={{
              width: 90,
              backgroundColor: '#05005B',
              borderColor: '#05005B',
              borderRadius: '16px',
            }}
          >
            Pesquisar
          </Button>
          <Button
            size="small"
            onClick={() => clearFilters()}
            style={{ width: 90, borderRadius: '16px' }}
          >
            Limpar
          </Button>
          <Button type="link" size="small" onClick={() => confirm({ closeDropdown: false })}>
            Filtrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#05005B' : undefined }} />
    ),
    onFilter: (value, record) => matchesTaskListColumnFilter(value, record, dataIndex),
  };
};
